import { isEmpty } from '@safety/shared-utils'

const HAZARD_LIVE_MESSAGE_TYPE_MAP = {
  0: '提交了安全隐患',
  1: '验收了安全隐患',
  2: '重新提交了安全隐患'
}

function pad2(num) {
  return String(num).padStart(2, '0')
}

function fmtTS(timestamp) {
  const timeObj = new Date(timestamp)
  return `${pad2(timeObj.getMonth() + 1)}-${pad2(timeObj.getDate())} ${pad2(timeObj.getHours())}:${pad2(timeObj.getMinutes())}`
}

export function formatUpdays(safeUpDays) {
  return String(safeUpDays).padStart(4, '0').split('')
}

export function getRiskScoreColor(score, riskLevelScores) {
  if (!riskLevelScores) return ''
  const [low, medium, high] = riskLevelScores
  if (score <= low) return 'rgba(0, 176, 240, 0.8)'
  if (score <= medium) return 'rgba(255, 255, 0, 0.8)'
  if (score <= high) return 'rgb(255, 192, 0, 0.8)'
  return 'rgba(255, 0, 0, 0.8)'
}

function formatJobLiveMessage(item, mapper) {
  const username = mapper.user[item.user]
  const tplname = mapper.tpl[item.tplid] || ''
  const timeStr = fmtTS(item.time)
  let message =
    `<span class="job-time">${timeStr}</span>:` +
    `员工<span class="job-user">${username}</span>` +
    `<span class="job-action">${item.type === 0 ? '申请了作业' : '结束了作业'}</span>` +
    `,作业票:<span class="job-name">${tplname}</span>`
  if (!isEmpty(item.areaId)) {
    message += `,区域:<span class="job-area">${mapper.area[item.areaId]?.name}</span>`
  }
  return message
}

function formatHazardLiveMessage(item, mapper) {
  const username = mapper.user[item.user]
  const timeStr = fmtTS(item.time)
  let message =
    `<span class="job-time">${timeStr}</span>:` +
    `员工<span class="job-user">${username}</span>` +
    `<span class="job-action">${HAZARD_LIVE_MESSAGE_TYPE_MAP[item.type]}</span>`
  if (!isEmpty(item.areaId)) {
    message += `,区域:<span class="job-area">${mapper.area[item.areaId]?.name}</span>`
  }
  return message
}

function formatRollingBoard(title, data, mapper, formatter) {
  return {
    title,
    data: data ? data.map((item) => ({ innerHTML: formatter(item, mapper), color: '', bgColor: '' })) : []
  }
}

export function formatKpiWidgetData(kpiId, value, ctx) {
  const { mapper, riskLevelScores, definition } = ctx
  if (definition?.percent) {
    return isEmpty(value) ? '0%' : `${(value * 100).toFixed(0)}%`
  }
  switch (kpiId) {
    case 1001:
      return {
        title: definition.name,
        data: value ? value.map(({ key, value: count }) => ({ name: mapper.hazardCategory[key], value: count })) : []
      }
    case 1002:
      return {
        title: definition.name,
        labels: value
          ? value.map(({ key }) => {
              const area = mapper.area[key]
              return area ? { name: area.name, fullname: `${area.mapName}/${area.name}` } : ''
            })
          : [],
        data: value ? value.map(({ value: count }) => count) : []
      }
    case 1003:
    case 1004:
    case 1017:
      return {
        title: definition.name,
        labels: value ? value.map(({ key }) => mapper.user[key]) : [],
        data: value ? value.map(({ value: count }) => (kpiId === 1003 ? count : Number(count).toFixed(0))) : []
      }
    case 1005:
      return {
        headers: ['排名', '区域', '风险值'],
        data: value
          ? value.map(({ key, value: score }, index) => ({
              cols: [index + 1, mapper.area[key]?.name, score],
              color: 'white',
              bgColor: getRiskScoreColor(score, riskLevelScores)
            }))
          : []
      }
    case 1006:
      return formatRollingBoard(definition.name, value, mapper, formatJobLiveMessage)
    case 1016:
      return formatRollingBoard(definition.name, value, mapper, formatJobLiveMessage)
    case 1007:
      return formatRollingBoard(definition.name, value, mapper, formatHazardLiveMessage)
    default:
      return value
  }
}

export function buildMapper(meta) {
  return {
    hazardCategory: meta.hazardCategories.reduce((acc, cur) => {
      acc[cur.id] = cur.name
      return acc
    }, {}),
    area: meta.areas.reduce((acc, cur) => {
      acc[cur.id] = cur
      return acc
    }, {}),
    user: meta.users.reduce((acc, cur) => {
      acc[cur.id] = cur.name
      return acc
    }, {}),
    tpl: meta.tpls.reduce((acc, cur) => {
      acc[cur.id] = cur.name
      return acc
    }, {})
  }
}

export function mapRiskLevels(riskLevels, areaMap) {
  return riskLevels.map((item) => ({
    tagId: areaMap[item.areaId].tagId,
    score: item.score,
    level: item.level
  }))
}

export function sortMapIds(mapIds, modelMap, areaMap) {
  const aggMapIds = []
  const orphanMapIds = []
  const normalMapMap = {}
  mapIds.forEach((mapId) => {
    const model = modelMap[mapId]
    if (!model) return
    const { type, parentAreaId } = model
    if (type === 0) {
      aggMapIds.push(mapId)
    } else if (type === 1) {
      if (isEmpty(parentAreaId)) {
        orphanMapIds.push(mapId)
      } else {
        const parentMapId = areaMap[parentAreaId]?.mapId
        if (isEmpty(parentMapId)) orphanMapIds.push(mapId)
        else {
          normalMapMap[parentMapId] = normalMapMap[parentMapId] || []
          normalMapMap[parentMapId].push(mapId)
        }
      }
    }
  })
  const ids = []
  aggMapIds.forEach((mapId) => {
    ids.push(mapId)
    if (normalMapMap[mapId]) {
      ids.push(...normalMapMap[mapId])
      delete normalMapMap[mapId]
    }
  })
  ids.push(...Object.values(normalMapMap).flat())
  ids.push(...orphanMapIds)
  return ids
}

export function createMapRuntimeState(mapIds) {
  const modelMap = {}
  mapIds.forEach((mapId) => {
    modelMap[mapId] = { data: null, riskLevels: [], loading: false, type: null, parentAreaId: null }
  })
  return modelMap
}

export function applyMapModels(modelMap, mapModels, areaMap) {
  mapModels.forEach((mapModel) => {
    if (!mapModel) return
    const { mapId, data, riskLevels, type, parentAreaId } = mapModel
    const runtime = modelMap[mapId]
    if (!runtime) return
    runtime.data = JSON.parse(data)
    runtime.riskLevels = mapRiskLevels(riskLevels, areaMap)
    runtime.type = type
    runtime.parentAreaId = parentAreaId
  })
}
